import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CATALOG_COURSES } from '../data/catalog';
import PageBackdrop from '../components/PageBackdrop';
import { PAGE_BACKGROUNDS } from '../constants/brand';
import usePageTitle from '../hooks/usePageTitle';

const showFreeBadge = (course) => course.is_free && course.course_id !== 'cyn_basics';

const Courses = () => {
  usePageTitle('Courses');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCourses(CATALOG_COURSES);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t border-b border-[#A80D1A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black film-grain relative overflow-hidden pt-32 pb-24">
      <PageBackdrop imageUrl={PAGE_BACKGROUNDS.courses} imageOpacity={0.5} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-20 max-w-3xl">
          <p className="chapter-label mb-6">COURSES</p>
          <h1 data-testid="courses-page-title" className="display-heading text-5xl sm:text-6xl lg:text-7xl text-white mb-6">
            Programs Designed For<br /><em>Future Filmmakers.</em>
          </h1>
          <p className="text-base leading-relaxed text-zinc-400">
            Explore programs across direction, editing, and foundations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {courses.map((course, idx) => (
            <Link
              key={course.course_id}
              to={`/courses/${course.course_id}`}
              data-testid={`course-card-${idx}`}
              className="group relative overflow-hidden bg-[#0D0D0D] aspect-[4/3] block"
            >
              <img
                src={course.thumbnail}
                alt={course.title}
                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/20"></div>

              <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-10">
                <div className="flex items-start justify-between">
                  <span className="font-serif text-6xl md:text-7xl text-white/70 group-hover:text-[#C8102E] transition-colors duration-500">
                    0{idx + 1}
                  </span>
                  {showFreeBadge(course) && <span className="level-badge level-free">Free</span>}
                </div>

                <div>
                  <h3 className="font-serif text-3xl md:text-4xl text-white mb-3 leading-tight">{course.title}</h3>
                  <p className="text-sm italic text-zinc-300 mb-6 font-serif">"{course.tagline}"</p>
                  <div className="flex items-center justify-end pt-4 border-t border-white/10">
                    <ArrowRight className="w-5 h-5 text-white group-hover:text-[#C8102E] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Courses;
